import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const queryClient = useQueryClient();

  // Fetch users
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/auth/users');
      return response.data;
    }
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await api.post('/auth/users', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowCreateModal(false);
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, userData }) => {
      const response = await api.put(`/auth/users/${userId}`, userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setEditingUser(null);
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      await api.delete(`/auth/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    }
  });

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-900/30 text-red-400',
      manager: 'bg-blue-900/30 text-blue-400',
      classify_user: 'bg-green-900/30 text-green-400',
      foia_user: 'bg-yellow-900/30 text-yellow-400'
    };
    return colors[role] || 'bg-[var(--vault-elevated)] text-[var(--text-muted)]';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Administration</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-[var(--vault-border)]">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'users'
                ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--vault-border)]'
            }`}
          >
            <UserIcon className="w-5 h-5 mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'audit'
                ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--vault-border)]'
            }`}
          >
            <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
            Audit Logs
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <UsersTab
          users={users}
          isLoading={usersLoading}
          getRoleColor={getRoleColor}
          onCreateUser={() => setShowCreateModal(true)}
          onEditUser={setEditingUser}
          onDeleteUser={handleDeleteUser}
          deleteLoading={deleteUserMutation.isLoading}
        />
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && <AuditLogsTab />}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(userData) => createUserMutation.mutate(userData)}
          isLoading={createUserMutation.isLoading}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={(userData) => updateUserMutation.mutate({ userId: editingUser.id, userData })}
          isLoading={updateUserMutation.isLoading}
        />
      )}
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users, isLoading, getRoleColor, onCreateUser, onEditUser, onDeleteUser, deleteLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--spotlight-gold)]"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end">
        <button
          onClick={onCreateUser}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-black bg-[var(--spotlight-gold)] hover:opacity-90"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add User
        </button>
      </div>

      <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--vault-border)]">
          <thead className="bg-[var(--vault-elevated)]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[var(--vault-surface)] divide-y divide-[var(--vault-border)]">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserIcon className="w-8 h-8 text-[var(--text-muted)]" />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-[var(--text-primary)]">{user.full_name}</div>
                      <div className="text-sm text-[var(--text-muted)]">{user.email}</div>
                      <div className="text-xs text-[var(--text-muted)]">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                    {user.role.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.is_active ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-muted)]">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button onClick={() => onEditUser(user)} className="text-[var(--spotlight-gold)] hover:text-[var(--spotlight-gold)] hover:opacity-80">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDeleteUser(user.id)}
                    className="text-red-400 hover:text-red-300"
                    disabled={deleteLoading}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Audit Logs Tab Component
const AuditLogsTab = () => {
  const [filters, setFilters] = useState({
    eventType: '',
    level: '',
    userId: '',
    limit: 50
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch audit logs
  const { data: auditData, isLoading, refetch } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', filters.limit);
      if (filters.eventType) params.append('event_type', filters.eventType);
      if (filters.level) params.append('level', filters.level);
      if (filters.userId) params.append('user_id', filters.userId);

      const response = await api.get(`/admin/audit/logs?${params.toString()}`);
      return response.data;
    },
    refetchInterval: 30000 // Auto-refresh every 30 seconds
  });

  // Fetch audit summary
  const { data: summary } = useQuery({
    queryKey: ['auditSummary'],
    queryFn: async () => {
      const response = await api.get('/admin/audit/logs/summary');
      return response.data;
    },
    refetchInterval: 60000
  });

  // Fetch event types for filter dropdown
  const { data: eventTypes } = useQuery({
    queryKey: ['auditEventTypes'],
    queryFn: async () => {
      const response = await api.get('/admin/audit/event-types');
      return response.data;
    }
  });

  const getLevelIcon = (level) => {
    switch (level) {
      case 'critical':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  const getResultIcon = (result) => {
    return result === 'SUCCESS'
      ? <CheckCircleIcon className="w-4 h-4 text-green-500" />
      : <XCircleIcon className="w-4 h-4 text-red-500" />;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatEventType = (eventType) => {
    return eventType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--spotlight-gold)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-4">
            <div className="text-sm font-medium text-[var(--text-muted)]">Total Events</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{summary.total_events}</div>
          </div>
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-4">
            <div className="text-sm font-medium text-[var(--text-muted)]">Critical Events</div>
            <div className="text-2xl font-bold text-red-400">{summary.by_level?.critical || 0}</div>
          </div>
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-4">
            <div className="text-sm font-medium text-[var(--text-muted)]">Warnings</div>
            <div className="text-2xl font-bold text-yellow-400">{summary.by_level?.warning || 0}</div>
          </div>
          <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-4">
            <div className="text-sm font-medium text-[var(--text-muted)]">Recent Failures</div>
            <div className="text-2xl font-bold text-orange-400">{summary.recent_failures?.length || 0}</div>
          </div>
        </div>
      )}

      {/* Filters and Refresh */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
              showFilters ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)] bg-[var(--vault-elevated)]' : 'border-[var(--vault-border)] text-[var(--text-secondary)] bg-[var(--vault-surface)] hover:bg-[var(--vault-elevated)]'
            }`}
          >
            <FunnelIcon className="w-4 h-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 border border-[var(--vault-border)] rounded-md text-sm font-medium text-[var(--text-secondary)] bg-[var(--vault-surface)] hover:bg-[var(--vault-elevated)]"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        <div className="text-sm text-[var(--text-muted)]">
          Showing {auditData?.logs?.length || 0} of {auditData?.total || 0} events
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-[var(--vault-elevated)] rounded-lg p-4 border border-[var(--vault-border)]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Event Type</label>
              <select
                value={filters.eventType}
                onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md text-sm bg-[var(--vault-bg)] text-[var(--text-primary)]"
              >
                <option value="">All Events</option>
                {eventTypes?.event_types?.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Level</label>
              <select
                value={filters.level}
                onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md text-sm bg-[var(--vault-bg)] text-[var(--text-primary)]"
              >
                <option value="">All Levels</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">User ID</label>
              <input
                type="text"
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                placeholder="Filter by user..."
                className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md text-sm bg-[var(--vault-bg)] text-[var(--text-primary)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Limit</label>
              <select
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md text-sm bg-[var(--vault-bg)] text-[var(--text-primary)]"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-[var(--vault-border)]">
          <thead className="bg-[var(--vault-elevated)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Level</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Timestamp</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Event</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Action</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Result</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-[var(--vault-surface)] divide-y divide-[var(--vault-border)]">
            {auditData?.logs?.map((log) => (
              <tr key={log.event_id} className={log.level === 'critical' ? 'bg-red-900/10' : log.level === 'warning' ? 'bg-yellow-900/10' : ''}>
                <td className="px-4 py-3 whitespace-nowrap">
                  {getLevelIcon(log.level)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--text-muted)]">
                  {formatTimestamp(log.timestamp)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {formatEventType(log.event_type)}
                  </span>
                  <div className="text-xs text-[var(--text-muted)]">{log.domain}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--text-primary)]">
                  {log.user_id || '-'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--text-secondary)]">
                  {log.action}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    {getResultIcon(log.result)}
                    <span className={`ml-1 text-sm ${log.result === 'SUCCESS' ? 'text-green-400' : 'text-red-400'}`}>
                      {log.result}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[var(--text-muted)] max-w-xs truncate">
                  {log.error_message || (log.source_ip ? `IP: ${log.source_ip}` : '-')}
                </td>
              </tr>
            ))}
            {(!auditData?.logs || auditData.logs.length === 0) && (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-[var(--text-muted)]">
                  No audit logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Info */}
      {auditData?.has_more && (
        <div className="text-center text-sm text-[var(--text-muted)]">
          Showing first {auditData.logs.length} of {auditData.total} events. Increase limit to see more.
        </div>
      )}
    </div>
  );
};

// Create User Modal Component
const CreateUserModal = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'classify_user'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border border-[var(--vault-border)] w-96 shadow-lg rounded-md bg-[var(--vault-surface)]">
        <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Create New User</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <input
            type="text"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
          >
            <option value="classify_user">Classify User</option>
            <option value="foia_user">FOIA User</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] bg-[var(--vault-elevated)] rounded-md hover:bg-[var(--vault-bg)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-black bg-[var(--spotlight-gold)] rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    full_name: user.full_name,
    role: user.role,
    is_active: user.is_active
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border border-[var(--vault-border)] w-96 shadow-lg rounded-md bg-[var(--vault-surface)]">
        <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Edit User</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <input
            type="text"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
            required
          />
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--vault-border)] rounded-md bg-[var(--vault-bg)] text-[var(--text-primary)]"
          >
            <option value="classify_user">Classify User</option>
            <option value="foia_user">FOIA User</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <label className="flex items-center text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="mr-2 accent-[var(--spotlight-gold)]"
            />
            Active User
          </label>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] bg-[var(--vault-elevated)] rounded-md hover:bg-[var(--vault-bg)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-black bg-[var(--spotlight-gold)] rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserManagement;
